import {
  ReadOnlyBooleanField,
  ReadOnlyField,
  ReadOnlyLinkField,
  ReadOnlyRichTextField,
} from "./ReadOnlyField";
import { Column, Row } from "@/components";
import { Label } from "@/components/ui/label";

import {
  communicationFrequencyOptions,
  communicationModeToLabel,
  stakeholderRoleToLabel,
  stakeholderValuePerceptionToLabel,
} from "@/lib/constants";
import { Stakeholder } from "@/types";

export const StakeholderReadOnly = ({
  stakeholder,
}: {
  stakeholder: Stakeholder;
}) => {
  const roleLabel =
    stakeholder.role && stakeholder.role.length > 0
      ? stakeholder.role.map((r) => stakeholderRoleToLabel[r]).join(", ")
      : null;

  const communicationModeLabel =
    stakeholder.communicationMode && stakeholder.communicationMode.length > 0
      ? stakeholder.communicationMode
          .map((m) => communicationModeToLabel[m])
          .join(", ")
      : null;
  const communicationFrequencyLabel =
    communicationFrequencyOptions.find(
      (o) => o.value === stakeholder.communicationFrequency,
    )?.label ?? null;

  const valuePerceptionLabel =
    (stakeholder.valuePerception ?? []).length > 0
      ? (stakeholder.valuePerception ?? [])
          .map((v) => stakeholderValuePerceptionToLabel[v])
          .join(", ")
      : null;

  return (
    <Column className="gap-12">
      {/* Basics */}
      <Column className="gap-2">
        <Label className="text-md font-semibold text-slate-700">Basics</Label>
        <Column className="gap-4">
          <Row className="gap-4">
            <ReadOnlyField
              className="flex-1"
              label="First Name"
              value={stakeholder.firstName}
            />
            <ReadOnlyField
              className="flex-1"
              label="Last Name"
              value={stakeholder.lastName}
            />
            <ReadOnlyField
              className="flex-1"
              label="Title"
              value={stakeholder.title}
            />
          </Row>
          <Row className="gap-4">
            <ReadOnlyField
              className="flex-1"
              label="Phone"
              value={stakeholder.phone}
            />
            <ReadOnlyField
              className="flex-1"
              label="Email"
              value={stakeholder.email}
            />
            <ReadOnlyLinkField
              className="flex-1"
              label="LinkedIn URL"
              url={stakeholder.linkedinUrl}
            />
          </Row>
          <ReadOnlyField label="Role" value={roleLabel} />
          <ReadOnlyRichTextField
            label="Personality and Notes"
            value={stakeholder.personalityAndNotes}
          />
        </Column>
      </Column>

      {/* Communication Preferences */}
      <Column className="gap-2">
        <Label className="text-md font-semibold text-slate-700">
          Communication Preferences
        </Label>
        <Column className="gap-4">
          <Row className="gap-4">
            <ReadOnlyBooleanField
              className="flex-1"
              label="Primary Contact"
              isSelected={stakeholder.primaryContact}
            />
            <ReadOnlyBooleanField
              className="flex-1"
              label="CC'd on Emails"
              isSelected={stakeholder.ccOnEmails}
            />
            <div className="flex-1" />
          </Row>
          <Row className="gap-4">
            <ReadOnlyField
              className="flex-1"
              label="Preferred Mode of Communication"
              value={communicationModeLabel}
            />
            <ReadOnlyField
              className="flex-1"
              infoTooltip="Assuming there are live requisitions"
              label="Preferred Communication Frequency"
              value={communicationFrequencyLabel}
            />
            <ReadOnlyField
              className="flex-1"
              label="Preferred Communication Style"
              value={stakeholder.communicationStyle}
            />
          </Row>
          <ReadOnlyRichTextField
            label="Communication Details"
            value={stakeholder.communicationDetails}
          />
        </Column>
      </Column>

      {/* Value Perception */}
      <Column className="gap-2">
        <Label className="text-md font-semibold text-slate-700">
          Value Perception
        </Label>
        <Column className="gap-4">
          <ReadOnlyField
            label="How do they perceive value"
            value={valuePerceptionLabel}
          />
          <ReadOnlyRichTextField
            label="Valued Candidate Qualities"
            value={stakeholder.valuedCandidateQualities}
          />
          <ReadOnlyRichTextField
            label="Common Rejection Reasons"
            value={stakeholder.commonRejectionReasons}
          />
        </Column>
      </Column>

      {/* View on External Partners */}
      <Column className="gap-2">
        <Label className="text-md font-semibold text-slate-700">
          View on External Partners
        </Label>
        <ReadOnlyRichTextField
          label="How do they view external partners?"
          value={stakeholder.viewOnExternalPartners}
        />
      </Column>
    </Column>
  );
};
